import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { USDLoader } from 'three/addons/loaders/USDLoader.js';
import { useTheme } from '../../../../common/hooks/use-theme';

const IDLE_TIMEOUT_MS = 3000;
const RECENTER_DURATION_S = 1.2;
const DRACO_DECODER_PATH = 'https://www.gstatic.com/draco/versioned/decoders/1.5.7/';

const easeInOutQuad = (t: number) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);

const getBgColor = () =>
  getComputedStyle(document.documentElement).getPropertyValue('--color-bg-card').trim();

const getExt = (src: string) => src.split('.').pop()?.toLowerCase() ?? '';

interface ModelViewerProps {
  src: string;
}

const ModelViewer: React.FC<ModelViewerProps> = ({ src }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const [loading, setLoading] = useState(true);
  const { isDark } = useTheme();

  useEffect(() => {
    // Defer one frame — ThemeProvider's effect applies data-theme after children's effects run
    const raf = requestAnimationFrame(() => {
      if (sceneRef.current) {
        sceneRef.current.background = new THREE.Color(getBgColor());
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [isDark]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(getBgColor());
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(
      45,
      container.clientWidth / container.clientHeight,
      0.01,
      10000
    );
    camera.position.set(0, 1.5, 3);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    container.appendChild(renderer.domElement);

    const resizeObserver = new ResizeObserver(() => {
      const { clientWidth: w, clientHeight: h } = container;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    });
    resizeObserver.observe(container);

    scene.add(new THREE.AmbientLight(0xffffff, 0.8));
    const dirLight = new THREE.DirectionalLight(0xffffff, 1.2);
    const backLight = new THREE.DirectionalLight(0xffffff, 1.0);
    dirLight.position.set(5, 10, 7.5);
    backLight.position.set(-3, 3, -8);
    scene.add(dirLight);
    scene.add(backLight);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.autoRotate = true;
    controls.autoRotateSpeed = 1.5;
    controls.enableDamping = true;

    let homePosition: THREE.Vector3 | null = null;
    let homeTarget: THREE.Vector3 | null = null;
    let isRecentering = false;
    let recenterT = 0;
    let recenterFromPos = new THREE.Vector3();
    let recenterFromTarget = new THREE.Vector3();
    let idleTimer: ReturnType<typeof setTimeout> | null = null;

    const triggerRecenter = () => {
      if (!homePosition || !homeTarget) return;
      isRecentering = true;
      recenterT = 0;
      recenterFromPos.copy(camera.position);
      recenterFromTarget.copy(controls.target);
    };

    const startIdleTimer = () => {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(triggerRecenter, IDLE_TIMEOUT_MS);
    };

    controls.addEventListener('start', () => {
      if (idleTimer) clearTimeout(idleTimer);
      isRecentering = false;
      controls.autoRotate = false;
    });

    controls.addEventListener('end', startIdleTimer);

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'f' || e.key === 'F') triggerRecenter();
    };
    window.addEventListener('keydown', onKeyDown);

    let frameId: number;
    const animate = () => {
      frameId = requestAnimationFrame(animate);

      if (isRecentering && homePosition && homeTarget) {
        recenterT = Math.min(recenterT + 1 / (RECENTER_DURATION_S * 60), 1);
        const t = easeInOutQuad(recenterT);
        camera.position.lerpVectors(recenterFromPos, homePosition, t);
        controls.target.lerpVectors(recenterFromTarget, homeTarget, t);
        if (recenterT >= 1) {
          isRecentering = false;
          controls.autoRotate = true;
        }
      }

      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    let loadedObject: THREE.Object3D | null = null;
    let cancelled = false;

    (async () => {
      try {
        const ext = getExt(src);

        if (ext === 'gltf' || ext === 'glb') {
          const dracoLoader = new DRACOLoader();
          dracoLoader.setDecoderPath(DRACO_DECODER_PATH);
          const gltfLoader = new GLTFLoader();
          gltfLoader.setDRACOLoader(dracoLoader);
          const gltf = await gltfLoader.loadAsync(src);
          dracoLoader.dispose();
          if (cancelled) return;
          loadedObject = gltf.scene;
        } else {
          const usdLoader = new USDLoader();
          loadedObject = await usdLoader.loadAsync(src);
          if (cancelled) return;
        }

        scene.add(loadedObject);

        const box = new THREE.Box3().setFromObject(loadedObject);
        const sphere = new THREE.Sphere();
        box.getBoundingSphere(sphere);

        if (!box.isEmpty() && sphere.radius > 0) {
          const fov = camera.fov * (Math.PI / 180);
          const dist = (sphere.radius / Math.sin(fov / 2));
          camera.position.copy(sphere.center).add(new THREE.Vector3(0, sphere.radius * 0.3, dist));
          camera.near = dist / 100;
          camera.far = dist * 100;
          camera.updateProjectionMatrix();
          controls.target.copy(sphere.center);
          controls.update();
        }

        homePosition = camera.position.clone();
        homeTarget = controls.target.clone();

        setLoading(false);
      } catch (error) {
        if (cancelled) return;
        console.error('[ModelViewer] load failed:', error);
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      sceneRef.current = null;
      resizeObserver.disconnect();
      if (idleTimer) clearTimeout(idleTimer);
      window.removeEventListener('keydown', onKeyDown);
      cancelAnimationFrame(frameId);
      controls.dispose();
      if (loadedObject) scene.remove(loadedObject);
      renderer.dispose();
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
    };
  }, [src]);

  return (
    <div className="relative w-full h-full">
      <div ref={containerRef} className="w-full h-full rounded-[22px] overflow-hidden" />
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-[32px] h-[32px] rounded-full border-[3px] border-blue-500 border-t-transparent animate-spin" />
        </div>
      )}
    </div>
  );
};

export default ModelViewer;
